import {Component, ViewChild} from '@angular/core';
import {ConvivesService} from '../../core/services/entities/convives.service';
import {HELP_FOLDERS, MSG_KEY, MSG_SEVERITY} from "../../core/constants";
import {ConviveDTO} from "../../core/dtos/convive-dto";
import {Subscription} from "rxjs";
import {GraphQLService} from "../../core/services/technique/graphql.service";
import {UtilsService} from "../../core/utils/utils.service";
import {saveAs} from "file-saver";
import {InternationalizationService} from "../../core/services/i8n/i8n.service";
import {ToastService} from "../../core/services/technique/toast.service";
import {ResponseWrapper} from "../../core/suppliers/wrappers/response-wrapper";
import {IsDeletableObject} from "../../core/models/is-deletable-object";
import {confirm} from "devextreme/ui/dialog";
import {OverlayPanel} from "primeng/overlaypanel";
import {Auth2Service} from "../../core/services/security/auth2.service";

@Component({
  selector: 'yo-convives',
  templateUrl: './convives.component.html',
  styleUrls: ['./convives.component.scss']
})
export class ConvivesComponent {

  prestations: ConviveDTO[] = [];

  pathFile: string = HELP_FOLDERS.REFERENTIEL + '/prestations/prestations';

  selectedRows: number[] = [];

  subGraphQL: Subscription;

  subXls: Subscription;

  errors: any[] = [];

  displayDeletePopupErrors: boolean = false;

  private subImport: Subscription;

  private subPrestationSaved: Subscription;

  displayImportPopupErrors: boolean = false;

  @ViewChild('opImportXls') opImportXls: OverlayPanel;

  constructor(public conviveSvc: ConvivesService,
              private readonly graphqlSvc: GraphQLService,
              private utilsSvc: UtilsService,
              private readonly i8nSvc: InternationalizationService,
              private readonly toastSvc: ToastService,
              private readonly auth2Svc: Auth2Service) {
  }

  ngOnInit() {
    this.fetchData();
    this.subscribePrestationSaved();
  }

  ngOnDestroy() {
    this.utilsSvc.unsubscribe(this.subGraphQL);
    this.utilsSvc.unsubscribe(this.subXls);
    this.utilsSvc.unsubscribe(this.subImport);
    this.utilsSvc.unsubscribe(this.subPrestationSaved);
  }

  fetchData = (): void => {
    const idsSites: number[] = this.auth2Svc.utilisateur.sites.map(s => s.id);
    this.subGraphQL = this.graphqlSvc.sendQuery(`
      {
          allPrestations(filters: {
          siteIds: [${idsSites}]
        }) {
              id,
              libelle,
              site { id, libelle },
              code,
              actif
          }
      }
    `)
      .subscribe((res) => {
        this.prestations = res.allPrestations;
      });
  }

  openDialogEdition = (prestation?: ConviveDTO): void => {
    this.conviveSvc.announceOpeningEditionDialog(prestation);
  }

  exportXslx = (): void => {
    this.subXls = this.conviveSvc.export(this.selectedRows)
      .subscribe(response => {
        const filename = `prestations-export-${new Date().getTime()}.xlsx`;
        const blob = new Blob([response], {type: 'application/vnd.ms-excel'});
        this.selectedRows = [];
        saveAs(blob, filename);
      });
  }

  canDelete = (): boolean => this.selectedRows?.length > 0;

  deleteValues = async (): Promise<void> => {
    this.errors = [];
    let idsToDeleteInTable = Object.assign([], this.selectedRows);

    let isDeletionValidated: boolean = await confirm(this.i8nSvc.getLabelFromCode(idsToDeleteInTable.length > 1 ? "CONFIRM_DELETION_PLURAL" : "CONFIRM_DELETION_SINGULAR", null), this.i8nSvc.getLabelFromCode("Suppression", null));

    if (!isDeletionValidated)
      return new Promise(null);

    this.conviveSvc.delete(this.selectedRows).subscribe(response => {
      const res: any = (response as ResponseWrapper<IsDeletableObject>).one;

      if (res) {
        if (res?.messagesErrorList?.length) {
          for (const error of res.messagesErrorList) {
            const labelsError: any = this.i8nSvc.getLabelFromCode(error.code, null);
            const infosLine: string = labelsError.replace('{}', error.args);
            this.errors.push({infosLine});
            const elementToKeep = this.prestations.find(u => u.code === error.args);
            idsToDeleteInTable = idsToDeleteInTable.filter(id => elementToKeep.id !== id);
          }
          this.displayDeleteErrors();
        } else {
          this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, idsToDeleteInTable.length > 1 ? `Les prestations ont bien été supprimées` : `La prestation a bien été supprimée`);
        }
        this.prestations = this.prestations.filter(prestation => !idsToDeleteInTable.find(id => id === prestation.id));
      }
    });
  }

  displayDeleteErrors = (): void => {
    this.displayDeletePopupErrors = true;
  }

  closeErrors = (): void => {
    this.errors = [];
    this.displayDeletePopupErrors = false;
    this.displayImportPopupErrors = false;
  }

  importXslx = ($event: any): void => {
    this.errors = [];
    if ($event.target.files && $event.target.files.length) {
      const file: File = $event.target.files[0];
      this.opImportXls.hide();
      this.subImport = this.conviveSvc.importFromXlsx(file)
        .subscribe((response) => {
          const res: any = response.one;
          if (res.allElementsImported) {
            this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, `Le fichier a été importé avec succès`);
          } else {
            for (const item of Object.entries(res.reports)) {
              let values: any[] = Array.of(item[1]);
              const labelsError: any[] = values.map(val => val.map(v => this.i8nSvc.getLabelFromCode(v.code, v.args.split(','))).join(', '));
              this.errors.push({infosLine: item[0], labelsError});
            }
          }

          for (const item of res.elementsImported) {
            let index = this.prestations.findIndex(atelier => atelier.id == item.id);
            if (index >= 0)
              this.prestations[index] = item;
            else
              this.prestations.push(item)
          }

          if (this.errors?.length)
            this.displayImportErrors();
        });
    }
  }

  calculateSiteValue = (prestation: ConviveDTO): string => prestation?.site?.libelle;

  displayImportErrors = (): void => {
    this.displayImportPopupErrors = true;
  }

  subscribePrestationSaved = (): void => {
    this.subPrestationSaved = this.conviveSvc.conviveSaved$
      .subscribe(() => this.fetchData());
  }

}
